import { createIsomorphicFn } from "@tanstack/react-start";
import { getRequestUrl } from "@tanstack/react-start/server";

// The URL the visitor asked for, **spelled as they spelled it** — path, query
// and hash, no origin.
//
// The router never hands a route that spelling: every `location` it builds
// re-serialises the query through the app's search codec
// (`router.parseLocation`), so `?share_token=abc=` reads back as
// `?share_token=abc%3D` and `%20` as `+`. For almost every route that is the
// same URL. For the legacy basepath redirects (#1022) it is not: they exist to
// hand a link from the wild on to its page **verbatim** — the share token
// unencoded and unaltered (ADR-0019 decision 5) — so they read the request
// itself.
//
// Isomorphic because a redirect runs on both sides. On a cold load (the case
// that matters: a link pasted or clicked from outside) the server reads the
// request line, which carries no hash — the browser keeps the fragment across
// the redirect itself. On a client navigation the address bar already holds the
// URL being entered; when it does not (a preload of a link the visitor has not
// followed), the router's own spelling of `fallback` is all there is.

/**
 * The client half, pure: the address bar's spelling of `fallback` when the bar
 * holds the URL being entered, else the router's own. Exported for its test.
 */
export function spelledHref(
  fallback: string,
  bar: Pick<Location, "pathname" | "search" | "hash">,
): string {
  const fallbackPath = fallback.split(/[?#]/, 1)[0];
  return fallbackPath === bar.pathname ? `${bar.pathname}${bar.search}${bar.hash}` : fallback;
}

export const requestedHref = createIsomorphicFn()
  .server((_fallback: string) => {
    const url = getRequestUrl();
    return `${url.pathname}${url.search}`;
  })
  .client((fallback: string) => spelledHref(fallback, window.location));
